import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, CircularProgress,
  Alert, Tooltip, Divider, ToggleButtonGroup, ToggleButton, useTheme,
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import ErrorIcon        from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ApartmentIcon    from '@mui/icons-material/Apartment';
import StorageIcon      from '@mui/icons-material/Storage';
import SyncIcon         from '@mui/icons-material/Sync';
import RefreshIcon      from '@mui/icons-material/Refresh';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';
import HistoryIcon      from '@mui/icons-material/History';
import LoginIcon        from '@mui/icons-material/Login';
import PeopleIcon       from '@mui/icons-material/People';
import confetti         from 'canvas-confetti';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, Legend } from 'recharts';
import api from '../utils/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Inject breathing animation CSS once
const PULSE_STYLE = `
@keyframes markerBreathe {
  0%,100% { transform: scale(1);    opacity: 1;   }
  50%      { transform: scale(1.35); opacity: 0.8; }
}
@keyframes markerRing {
  0%   { transform: scale(0.5); opacity: 0.7; }
  100% { transform: scale(2.2); opacity: 0;   }
}
.marker-pulse-ring {
  position: absolute; inset: 0; border-radius: 50%;
  animation: markerRing 2.4s ease-out infinite;
}
.marker-dot {
  position: relative; border-radius: 50%;
  animation: markerBreathe 2.4s ease-in-out infinite;
  z-index: 1;
}
`;
if (!document.getElementById('marker-pulse-style')) {
  const el = document.createElement('style');
  el.id = 'marker-pulse-style';
  el.textContent = PULSE_STYLE;
  document.head.appendChild(el);
}

const makeIcon = (color, pulse = false) => L.divIcon({
  className: '',
  html: `
    <div style="position:relative;width:36px;height:36px;display:flex;align-items:center;justify-content:center;">
      <div class="marker-pulse-ring" style="background:${color};"></div>
      <div class="marker-dot" style="
        width:16px;height:16px;background:${color};
        border:2.5px solid rgba(255,255,255,0.95);
        box-shadow:0 2px 10px rgba(0,0,0,0.5);
      "></div>
    </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

// Auto-fit map to all markers
function FitBounds({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (!locations || locations.length === 0) return;
    if (locations.length === 1) {
      map.setView([locations[0].latitude, locations[0].longitude], 10);
    } else {
      const bounds = L.latLngBounds(locations.map(t => [t.latitude, t.longitude]));
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 10, minZoom: 6 });
    }
  }, [map, locations]);
  return null;
}

const STATUS_COLOR = { healthy: '#22c55e', offline: '#ef4444', degraded: '#f59e0b', unknown: '#6b778c' };
const DEPLOY_COLOR = { success: '#22c55e', failed: '#ef4444', running: '#3b82f6', pending: '#f59e0b' };

const fmt = iso => iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-';

const timeAgo = iso => {
  if (!iso) return '-';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const fmtUptime = s => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// ── sub-components ─────────────────────────────────────────────────────────

const Card = ({ children, sx = {} }) => {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', height: '100%', ...sx }}>
      {children}
    </Paper>
  );
};

const CardTitle = ({ children, action }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ fontSize: '0.875rem', letterSpacing: 0.1 }}>
        {children}
      </Typography>
      {action}
    </Box>
  );
};

const StatCard = ({ icon, label, value, color, sub, pulse }) => (
  <Card>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 50, height: 50, borderRadius: '14px',
        backgroundColor: `${color}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        ...(pulse ? {
          animation: 'pulse 2s infinite',
          '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
        } : {}),
      }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight={800} color="text.primary" lineHeight={1.1} sx={{ fontSize: '1.75rem' }}>
          {value ?? '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.25} sx={{ fontSize: '0.78rem' }}>
          {label}
        </Typography>
        {sub && (
          <Typography variant="caption" sx={{ color, fontWeight: 600 }}>{sub}</Typography>
        )}
      </Box>
    </Box>
  </Card>
);

const DbDot = ({ status }) => (
  <Box sx={{
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%', mr: 0.5,
    backgroundColor: status === 'ok' ? '#22c55e' : '#ef4444',
  }} />
);

const RowDivider = () => (
  <Box sx={{ borderBottom: 1, borderColor: 'divider', '&:last-child': { border: 0 } }} />
);

// ── main ───────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const theme = useTheme();
  const dark  = theme.palette.mode === 'dark';

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [pinging, setPinging]     = useState(false);
  const [error, setError]         = useState('');
  const [refreshMs, setRefreshMs] = useState(30000);
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef    = useRef(null);
  const prevUsersRef = useRef(null);

  const fireCelebration = () => {
    const end = Date.now() + 3000;
    const colors = ['#10b981','#3b82f6','#f59e0b','#ec4899','#a855f7'];
    const frame = () => {
      confetti({ particleCount: 6, angle: 60,  spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 6, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/dashboard');
      const newTotal = r.data?.stats?.totalUsers ?? 0;
      // Fire confetti when crossing a 1000-user milestone
      if (prevUsersRef.current !== null && newTotal > 0) {
        const prevMilestone = Math.floor(prevUsersRef.current / 1000);
        const newMilestone  = Math.floor(newTotal / 1000);
        if (newMilestone > prevMilestone) fireCelebration();
      }
      prevUsersRef.current = newTotal;
      setData(r.data);
      setLastRefresh(new Date());
    } catch { setError('Failed to load dashboard'); }
    finally { if (!silent) setLoading(false); }
  }, []);

  const pingAll = async () => {
    setPinging(true);
    try { await api.post('/tenants/ping-all'); await load(true); }
    catch {}
    finally { setPinging(false); }
  };

  useEffect(() => {
    load();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load(true), refreshMs);
    return () => clearInterval(timerRef.current);
  }, [load, refreshMs]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;

  const { stats, lastSyncs, lastSyncOverall, recentDeploys, tenantStatuses, versionDist, recentActivity, adminUptime, loginsByTenant } = data;

  const pieData = (tenantStatuses || []).map(s => ({
    name: s.status.charAt(0).toUpperCase() + s.status.slice(1),
    value: s.count,
    color: STATUS_COLOR[s.status] || '#9aa5b4',
  }));

  const rowSx = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    py: 1, borderBottom: '1px solid', borderColor: 'divider',
    '&:last-child': { border: 0 },
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Dashboard</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            HOA Platform Control Center
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {lastRefresh && (
            <Typography variant="caption" color="text.secondary">
              Updated {timeAgo(lastRefresh)}
            </Typography>
          )}
          <ToggleButtonGroup
            size="small" exclusive
            value={refreshMs}
            onChange={(_, v) => { if (v) setRefreshMs(v); }}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5, py: 0.4, fontSize: '0.72rem', fontWeight: 600,
                borderColor: theme.palette.divider,
                color: 'text.secondary',
                '&.Mui-selected': {
                  backgroundColor: dark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
                  color: '#3b82f6',
                },
              },
            }}
          >
            <ToggleButton value={10000}>10s</ToggleButton>
            <ToggleButton value={30000}>30s</ToggleButton>
            <ToggleButton value={60000}>1m</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined" size="small"
            startIcon={pinging ? <CircularProgress size={13} /> : <RefreshIcon />}
            onClick={pingAll} disabled={pinging}
            sx={{ borderColor: theme.palette.divider, color: '#3b82f6' }}
          >
            {pinging ? 'Pinging…' : 'Ping All'}
          </Button>
          <Button
            variant="outlined" size="small" startIcon={<RefreshIcon />}
            onClick={() => load(true)}
            sx={{ borderColor: theme.palette.divider, color: 'text.secondary' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          { icon: <ApartmentIcon />, label: 'Total Tenants', value: stats.totalTenants, color: '#3b82f6' },
          { icon: <CheckCircleIcon />, label: 'Healthy', value: stats.healthyTenants, color: '#22c55e' },
          { icon: <WarningAmberIcon />, label: 'Degraded', value: stats.degradedTenants, color: '#f59e0b', pulse: stats.degradedTenants > 0 },
          { icon: <ErrorIcon />, label: 'Offline', value: stats.offlineTenants, color: '#ef4444', pulse: stats.offlineTenants > 0 },
          {
            icon: <StorageIcon />, label: 'DB Issues', color: '#a855f7',
            value: stats.db1Down + stats.db2Down,
            sub: stats.db1Down + stats.db2Down > 0 ? `DB1:${stats.db1Down} DB2:${stats.db2Down}` : 'All healthy',
            pulse: stats.db1Down + stats.db2Down > 0,
          },
          { icon: <AccessTimeIcon />, label: 'Admin Uptime', value: fmtUptime(adminUptime), color: '#06b6d4' },
          { icon: <LoginIcon />, label: 'Logins (24h)', value: stats.totalLogins24h ?? '—', color: '#10b981', sub: 'across all tenants' },
          { icon: <PeopleIcon />, label: 'Total Users', value: stats.totalUsers ?? '—', color: '#a855f7', sub: 'across all tenants' },
        ].map((s, i) => (
          <Grid key={i} item xs={6} sm={4} md={2}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Logins + Users per tenant breakdown */}
      {(loginsByTenant || []).length > 0 && (
        <Card sx={{ mb: 2.5 }}>
          <CardTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PeopleIcon sx={{ fontSize: 16, color: '#a855f7' }} />
              Users &amp; Activity by Tenant
            </Box>
          </CardTitle>
          <Box sx={{ display: 'flex', px: 0.5, pb: 0.5 }}>
            <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>Tenant</Typography>
            <Typography variant="caption" sx={{ width: 80, textAlign: 'right', color: '#a855f7' }}>Users</Typography>
            <Typography variant="caption" sx={{ width: 90, textAlign: 'right', color: '#10b981' }}>Logins (24h)</Typography>
          </Box>
          {(loginsByTenant || []).map(t => (
            <Box key={t.slug} sx={{ ...rowSx, px: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{t.name}</Typography>
              <Chip
                label={t.user_count ?? '—'}
                size="small"
                sx={{ width: 80, justifyContent: 'center', bgcolor: '#a855f722', color: '#a855f7', fontWeight: 600 }}
              />
              <Chip
                label={t.logins_24h ?? 0}
                size="small"
                sx={{ width: 90, justifyContent: 'center', ml: 0.5, bgcolor: t.logins_24h > 0 ? '#10b98122' : 'transparent', color: t.logins_24h > 0 ? '#10b981' : '#6b778c', fontWeight: 600 }}
              />
            </Box>
          ))}
        </Card>
      )}

      {/* Map */}
      {(data.tenantLocations || []).length > 0 && (
        <Card sx={{ mb: 2.5 }}>
          <CardTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ApartmentIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
              Tenant Locations
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {Object.entries(STATUS_COLOR).map(([s, c]) => (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c }} />
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize', fontSize: '0.72rem' }}>{s}</Typography>
                </Box>
              ))}
            </Box>
          </CardTitle>
          <Box sx={{ height: 420, borderRadius: '12px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <MapContainer
              center={[28.5, -82]}
              zoom={7}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
              zoomControl
            >
              <TileLayer
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                url={dark
                  ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                  : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'}
              />
              <FitBounds locations={data.tenantLocations} />
              {data.tenantLocations.map(t => {
                const color = STATUS_COLOR[t.status] || '#6b778c';
                return (
                  <Marker key={t.slug} position={[t.latitude, t.longitude]} icon={makeIcon(color, true)}>
                    <Popup>
                      <Box sx={{ minWidth: 160 }}>
                        <Typography fontWeight={700} fontSize="0.85rem" color="text.primary">{t.name}</Typography>
                        <Typography fontSize="0.75rem" color="text.secondary" mt={0.25}>{t.domain}</Typography>
                        {t.address && <Typography fontSize="0.73rem" color="text.secondary">{t.address}</Typography>}
                        <Box sx={{ display: 'inline-block', mt: 0.75, px: 1, py: 0.25, borderRadius: '999px', backgroundColor: `${color}22` }}>
                          <Typography fontSize="0.72rem" fontWeight={700} sx={{ color, textTransform: 'capitalize' }}>{t.status}</Typography>
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </Box>
        </Card>
      )}

      {/* Row 2 */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {/* Pie chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardTitle>Tenant Health</CardTitle>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={48} outerRadius={76} paddingAngle={3} dataKey="value">
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <RTooltip
                  formatter={(v, n) => [`${v} tenant${v !== 1 ? 's' : ''}`, n]}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: '0.8rem',
                    color: theme.palette.text.primary,
                  }}
                />
                <Legend
                  iconType="circle" iconSize={7}
                  formatter={v => <span style={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Last sync per tenant */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SyncIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                Last Sync
              </Box>
            </CardTitle>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              Overall:{' '}
              <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
                {timeAgo(lastSyncOverall)}
              </Typography>
            </Typography>
            <Box>
              {(lastSyncs || []).length === 0
                ? <Typography variant="body2" color="text.secondary">No syncs yet</Typography>
                : lastSyncs.map(s => (
                  <Box key={s.slug} sx={rowSx}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="text.primary">{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.version ? `v${s.version}` : '-'}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip label={s.status || '-'} size="small"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700,
                          backgroundColor: `${DEPLOY_COLOR[s.status] || '#9aa5b4'}22`,
                          color: DEPLOY_COLOR[s.status] || '#9aa5b4' }} />
                      <Typography variant="caption" color="text.secondary" display="block">{timeAgo(s.finished_at)}</Typography>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Card>
        </Grid>

        {/* DB status */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <StorageIcon sx={{ fontSize: 16, color: '#a855f7' }} />
                Database Status
              </Box>
            </CardTitle>
            <Box>
              {(data.tenantDbSummary || []).map(t => {
                const db1 = t.db1 || {};
                const db2 = t.db2 || {};
                const allOk = db1.status === 'ok' && db2.status === 'ok';
                return (
                  <Box key={t.name} sx={rowSx}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">{t.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Tooltip title={db1.status === 'ok' ? 'DB1 OK' : `DB1: ${db1.error || 'down'}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <DbDot status={db1.status} />
                          <Typography variant="caption" color="text.secondary">DB1</Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip title={db2.status === 'ok' ? 'DB2 OK' : `DB2: ${db2.error || 'down'}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <DbDot status={db2.status} />
                          <Typography variant="caption" color="text.secondary">DB2</Typography>
                        </Box>
                      </Tooltip>
                      {!allOk && <WarningAmberIcon sx={{ fontSize: 13, color: '#f59e0b' }} />}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Row 3 */}
      <Grid container spacing={2.5}>
        {/* Recent syncs */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SyncIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                Recent Syncs
              </Box>
            </CardTitle>
            {(recentDeploys || []).length === 0
              ? <Typography variant="body2" color="text.secondary">No syncs yet</Typography>
              : recentDeploys.map(d => (
                <Box key={d.id} sx={rowSx}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">{d.tenant_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.version ? `v${d.version}` : '-'} · {fmt(d.started_at)}
                      {d.triggered_by_name ? ` · ${d.triggered_by_name}` : ''}
                    </Typography>
                  </Box>
                  <Chip label={d.status} size="small"
                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700,
                      backgroundColor: `${DEPLOY_COLOR[d.status] || '#9aa5b4'}22`,
                      color: DEPLOY_COLOR[d.status] || '#9aa5b4' }} />
                </Box>
              ))}
          </Card>
        </Grid>

        {/* Recent activity */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <HistoryIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                Recent Activity
              </Box>
            </CardTitle>
            {(recentActivity || []).length === 0
              ? <Typography variant="body2" color="text.secondary">No activity</Typography>
              : recentActivity.map((a, i) => (
                <Box key={i} sx={rowSx}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">{a.action}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.username || 'system'}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{timeAgo(a.created_at)}</Typography>
                </Box>
              ))}
          </Card>
        </Grid>

        {/* Versions + uptime */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardTitle>Versions</CardTitle>
            {(versionDist || []).length === 0
              ? <Typography variant="body2" color="text.secondary">No data yet</Typography>
              : versionDist.map(v => (
                <Box key={v.current_version} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography variant="body2" fontWeight={700} color="text.primary" fontFamily="monospace">
                    v{v.current_version}
                  </Typography>
                  <Chip label={`${v.count} tenant${v.count !== 1 ? 's' : ''}`} size="small"
                    sx={{ height: 20, fontSize: '0.68rem',
                      backgroundColor: dark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)',
                      color: '#3b82f6', fontWeight: 700 }} />
                </Box>
              ))}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">Admin uptime</Typography>
              <Typography variant="caption" fontWeight={700} color="#06b6d4">{fmtUptime(adminUptime)}</Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
